import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CalendarDays, LogOut, ArrowLeft, ExternalLink, Sparkles, Globe } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useBilling } from '../contexts/BillingContext';
import { useAuth } from '../App';

/**
 * iOS App Store compliance surfaces.
 *
 * Rules baked in here (Feb 2026):
 *   • No prices, no purchase CTAs, no subscription-change controls.
 *   • Show the tenant's REAL plan + renewal date pulled from the live
 *     BillingContext so users don't wonder if the app is stale.
 *   • Point users at akountfly.com/billing (with ?src=email so AASA
 *     Universal Links fall through to the browser) whenever they need
 *     to take an action.
 *
 * This page currently backs /pricing on iOS (see App.js). /billing on
 * iOS renders the full BillingDashboardPage in read-only mode instead.
 */

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return null; }
};

const openManageOnWeb = () => {
  // src=email forces AASA to fall through to the browser even though
  // the app claims /billing — the user needs the mutation surface.
  const url = 'https://akountfly.com/billing?src=email';
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    window.location.href = url;
  }
};

export default function IOSAccountPage() {
  const navigate = useNavigate();
  const { billingStatus } = useBilling() || {};
  const planName = billingStatus?.plan_name || billingStatus?.plan_id || null;
  const status = billingStatus?.status || null;
  const renewsOn = fmtDate(billingStatus?.next_billing_date || billingStatus?.current_period_end);
  const trialEndsOn = fmtDate(billingStatus?.trial_ends_at);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8" data-testid="ios-account-page">
      <Card className="w-full max-w-sm p-6 space-y-5">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#283F8B]/10 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-[#283F8B]" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mt-3">Your account</h1>
          {planName ? (
            <p className="text-sm text-slate-600 mt-1.5" data-testid="ios-account-plan">
              Current plan: <span className="font-semibold text-slate-900">{planName}</span>
              {status && (
                <span className="ml-1 text-xs uppercase tracking-wider text-slate-400">· {status.replace(/_/g, ' ')}</span>
              )}
            </p>
          ) : (
            <p className="text-sm text-slate-600 mt-1.5">Account details are available by email.</p>
          )}
          {(trialEndsOn || renewsOn) && (
            <p
              className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1"
              data-testid="ios-account-renewal"
            >
              <CalendarDays className="w-3.5 h-3.5" />
              {trialEndsOn
                ? <>Trial ends {trialEndsOn}</>
                : <>Renews on {renewsOn}</>}
            </p>
          )}
        </div>

        {/* Feb 2026 · A detailed usage breakdown lives on /billing (iOS
            read-only) — this stub sticks to plan + renewal + a nudge
            to the web for any actionable change. */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/billing')}
          className="w-full rounded-full"
          data-testid="ios-account-view-usage-btn"
        >
          View plan &amp; usage details
        </Button>

        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5" data-testid="ios-account-manage-on-web">
          <div className="flex items-start gap-2.5">
            <Globe className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-900">Manage on the web</p>
              <p className="text-[11px] text-amber-800 leading-relaxed mt-0.5">
                Plans, pricing, cards and cancellations are handled at
                <span className="font-semibold whitespace-nowrap"> akountfly.com/billing</span> — same email, no
                new password.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={openManageOnWeb}
            data-testid="ios-account-open-web-btn"
            className="w-full mt-3 rounded-full border-amber-300 text-amber-900 hover:bg-amber-100"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in browser
          </Button>
        </div>

        <Button
          variant="ghost"
          className="w-full rounded-full text-slate-700"
          onClick={() => navigate('/dashboard')}
          data-testid="ios-account-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to dashboard
        </Button>
      </Card>
    </div>
  );
}

export function IOSSetupPendingPage() {
  const { logout, business, refreshBusiness } = useAuth() || {};
  const navigate = useNavigate();
  // Self-heal: the business may simply not have loaded yet (cold start,
  // wiped cache). Re-check and leave as soon as it is there.
  useEffect(() => {
    if (business) { navigate('/dashboard', { replace: true }); return undefined; }
    let tries = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try { if (refreshBusiness) await refreshBusiness(); } catch { /* noop */ }
    };
    tick();
    const id = setInterval(() => { if (tries < 8) tick(); else clearInterval(id); }, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [business, refreshBusiness, navigate]);
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="ios-setup-pending-page">
      <Card className="w-full max-w-sm p-6 text-center space-y-4">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#283F8B]/10 flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-[#283F8B]" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-slate-900">Account setup pending</h1>
          <p className="text-sm text-slate-600 mt-2">
            Your account setup isn't finished yet. Once setup is complete, everything will be ready here automatically.
          </p>
        </div>
        {logout && (
          <Button variant="outline" className="w-full rounded-full" onClick={logout} data-testid="ios-setup-signout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        )}
      </Card>
    </div>
  );
}
